import React, { useEffect, useMemo, useRef, useState } from "react";
import { Heart, Check, ChevronDown, MapPin, BookHeart, Sparkles, Sun, ImagePlus, Plus, X } from "lucide-react";
import { useBdayChecklist, useBdayState } from "../../contexts/BdayStateContext";

/* --------- customize her name here --------- */
const HER_NAME = "Her";
const BIRTHDAY_ISO = "2026-07-08T09:00:00";

/* --------- helpers --------- */
function useCountdown(targetIso: string) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);
  const target = useMemo(() => new Date(targetIso).getTime(), [targetIso]);
  const diff = Math.max(0, target - now);
  const days = Math.floor(diff / 86_400_000);
  const hours = Math.floor((diff % 86_400_000) / 3_600_000);
  const minutes = Math.floor((diff % 3_600_000) / 60_000);
  const seconds = Math.floor((diff % 60_000) / 1000);
  return { days, hours, minutes, seconds, done: diff === 0 };
}

/* --------- checklist primitive --------- */
function CheckItem({ id, label, checked, onToggle }: { id: string; label: string; checked: boolean; onToggle: () => void }) {
  return (
    <label htmlFor={id} className="group flex cursor-pointer items-center gap-3 rounded-xl px-3 py-2 transition-colors hover:bg-blush/40">
      <span className={`grid h-5 w-5 shrink-0 place-items-center rounded-md border transition-all ${checked ? "border-gold bg-gold text-warm-white" : "border-charcoal/25 bg-warm-white"}`}>
        {checked && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
      </span>
      <input id={id} type="checkbox" className="sr-only" checked={checked} onChange={onToggle} />
      <span className={`text-sm leading-snug transition-all ${checked ? "text-muted-foreground line-through" : "text-charcoal"}`}>{label}</span>
    </label>
  );
}

function useChecklist(key: string, items: string[]) {
  return useBdayChecklist(key, items);
}

/* --------- floating hearts --------- */
function FloatingHearts() {
  const hearts = useMemo(
    () => Array.from({ length: 8 }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 12,
      duration: 12 + Math.random() * 10,
      size: 12 + Math.random() * 14,
      opacity: 0.25 + Math.random() * 0.35,
    })),
    []
  );
  return (
    <div aria-hidden className="pointer-events-none fixed inset-0 z-0 overflow-hidden">
      {hearts.map((h) => (
        <Heart
          key={h.id}
          className="absolute text-blush-deep"
          style={{
            left: `${h.left}%`,
            bottom: 0,
            width: h.size,
            height: h.size,
            opacity: h.opacity,
            animation: `bday-float-up ${h.duration}s linear ${h.delay}s infinite`,
          }}
          fill="currentColor"
          strokeWidth={0}
        />
      ))}
    </div>
  );
}

/* --------- countdown --------- */
function Countdown() {
  const { days, hours, minutes, seconds, done } = useCountdown(BIRTHDAY_ISO);
  const units = [
    { label: "Days", value: days },
    { label: "Hours", value: hours },
    { label: "Minutes", value: minutes },
    { label: "Seconds", value: seconds },
  ];
  return (
    <div className="mx-auto grid max-w-md grid-cols-2 gap-2 sm:flex sm:max-w-xl sm:flex-wrap sm:gap-4">
      {units.map((u) => (
        <div key={u.label} className="glass-card rounded-2xl px-3 py-3 text-center sm:min-w-[92px] sm:flex-1 sm:px-5 sm:py-4">
          <div className="text-serif text-2xl font-semibold text-charcoal sm:text-4xl">
            {done ? "0" : String(u.value).padStart(2, "0")}
          </div>
          <div className="mt-1 text-[10px] uppercase tracking-[0.24em] text-muted-foreground sm:text-xs">{u.label}</div>
        </div>
      ))}
    </div>
  );
}

/* --------- progress bar (global) --------- */
function ProgressBar({ percent }: { percent: number }) {
  return (
    <div className="fixed top-0 left-0 right-0 z-50 h-1.5 bg-cream/60 backdrop-blur">
      <div
        className="h-full bg-gradient-to-r from-blush-deep via-gold to-blush-deep transition-all duration-500 ease-out"
        style={{ width: `${percent}%` }}
      />
    </div>
  );
}

/* --------- timeline card --------- */
type TimelineCardProps = {
  time?: string;
  emoji: string;
  title: string;
  description?: string;
  id: string;
  checked: boolean;
  onToggle: () => void;
  featured?: boolean;
  children?: React.ReactNode;
};
function TimelineCard({ emoji, title, description, id, checked, onToggle, featured, children, time }: TimelineCardProps) {
  return (
    <div className="relative pl-9 sm:pl-14">
      <button
        aria-label={checked ? `Mark ${title} incomplete` : `Mark ${title} complete`}
        onClick={onToggle}
        className={`absolute left-0 top-3 grid h-7 w-7 shrink-0 place-items-center rounded-full border-2 transition-all sm:h-8 sm:w-8 sm:left-2 ${
          checked ? "border-gold bg-gold text-warm-white shadow-glow" : "border-blush-deep bg-warm-white text-blush-deep active:scale-95"
        }`}
      >
        {checked ? <Check className="h-3.5 w-3.5 sm:h-4 sm:w-4" strokeWidth={3} /> : <span className="text-sm leading-none sm:text-base">{emoji}</span>}
      </button>
      <div
        className={`glass-card rounded-2xl p-4 transition-all sm:rounded-3xl sm:p-7 ${featured ? "border-gold/60 shadow-elegant" : ""} ${checked ? "opacity-70" : ""}`}
      >
        <div className="min-w-0">
          {time && <div className="text-[10px] uppercase tracking-[0.15em] text-gold sm:text-xs sm:tracking-[0.2em]">{time}</div>}
          <h3 className={`text-serif mt-1 text-lg font-semibold leading-snug text-charcoal sm:text-2xl ${checked ? "line-through decoration-gold/60" : ""}`}>
            <span className="mr-1.5 sm:mr-2">{emoji}</span>{title}
          </h3>
          {description && <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{description}</p>}
        </div>
        <input id={id} type="checkbox" checked={checked} onChange={onToggle} className="sr-only" />
        {children && <div className="mt-4 sm:mt-5">{children}</div>}
      </div>
    </div>
  );
}

/* --------- section header --------- */
function SectionHeader({ day, date, theme, quote }: { day: string; date: string; theme: string; quote?: string }) {
  return (
    <div className="mx-auto max-w-3xl px-4 text-center sm:px-6">
      <div className="text-[10px] uppercase tracking-[0.2em] text-gold sm:text-xs sm:tracking-[0.32em]">{day} · {date}</div>
      <h2 className="text-serif mt-3 text-3xl font-semibold leading-tight text-charcoal sm:text-5xl md:text-6xl">{theme}</h2>
      {quote && <p className="text-serif mt-3 text-base italic text-muted-foreground sm:mt-4 sm:text-lg">{quote}</p>}
      <div className="gold-divider mx-auto mt-5 w-20 sm:mt-6 sm:w-24" />
    </div>
  );
}

/* --------- shopping category card --------- */
function ShoppingCategory({ title, items: defaultItems, storageKey }: { title: string; items: string[]; storageKey: string }) {
  const [open, setOpen] = useState(true);
  const [newItem, setNewItem] = useState("");
  const { getShoppingItems, getShoppingRemoved, addShoppingItem, removeShoppingItem } = useBdayState();
  const customItems = getShoppingItems(storageKey);
  const removedItems = getShoppingRemoved(storageKey);
  const allItems = useMemo(() => {
    const removed = new Set(removedItems);
    return [...defaultItems, ...customItems.filter((item) => !defaultItems.includes(item))].filter(
      (item) => !removed.has(item)
    );
  }, [defaultItems, customItems, removedItems]);
  const cl = useChecklist(storageKey, allItems);
  const percent = cl.total > 0 ? Math.round((cl.completed / cl.total) * 100) : 0;

  const handleAdd = () => {
    if (!newItem.trim()) return;
    addShoppingItem(storageKey, newItem);
    setNewItem("");
  };

  return (
    <div className="glass-card rounded-2xl p-4 sm:rounded-3xl sm:p-6">
      <button
        onClick={() => setOpen((o) => !o)}
        className="grid w-full grid-cols-[minmax(0,1fr)_auto] items-center gap-2 text-left sm:gap-3"
      >
        <div className="min-w-0">
          <div className="text-serif text-lg font-semibold text-charcoal sm:text-xl">{title}</div>
          <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-blush/60">
            <div className="h-full bg-gradient-to-r from-blush-deep to-gold transition-all" style={{ width: `${percent}%` }} />
          </div>
          <div className="mt-1 text-xs text-muted-foreground">{cl.completed} of {cl.total}</div>
        </div>
        <ChevronDown className={`h-5 w-5 shrink-0 text-charcoal/60 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <>
          <div className="mt-4 grid grid-cols-1 gap-1 sm:grid-cols-2">
            {allItems.map((item) => (
              <div key={item} className="flex items-center gap-1">
                <div className="min-w-0 flex-1">
                  <CheckItem
                    id={`${storageKey}-${item}`}
                    label={item}
                    checked={!!cl.state[item]}
                    onToggle={() => cl.toggle(item)}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => removeShoppingItem(storageKey, item)}
                  className="grid h-8 w-8 shrink-0 place-items-center rounded-full text-charcoal/50 transition-colors hover:bg-blush/50 hover:text-charcoal"
                  aria-label={`Remove ${item}`}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
          <div className="mt-4 flex gap-2">
            <input
              type="text"
              value={newItem}
              onChange={(e) => setNewItem(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleAdd())}
              placeholder="Add an item…"
              className="min-w-0 flex-1 rounded-xl border border-blush/70 bg-warm-white/80 px-3 py-2.5 text-sm text-charcoal placeholder:text-muted-foreground/70 focus:border-gold focus:outline-none focus:ring-2 focus:ring-gold/30"
            />
            <button
              type="button"
              onClick={handleAdd}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-xl bg-charcoal px-3 py-2.5 text-xs font-medium text-warm-white transition-colors hover:bg-blush-deep sm:px-4 sm:text-sm"
            >
              <Plus className="h-3.5 w-3.5" />
              Add
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/* --------- Map placeholder --------- */
function MapPlaceholder({ place }: { place: string }) {
  return (
    <div className="mt-4 flex items-start gap-3 rounded-2xl border border-dashed border-sage/80 bg-sage/25 px-3 py-3 text-sm text-charcoal sm:px-4">
      <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-charcoal/70" />
      <span className="min-w-0 break-words"><span className="font-medium">{place}</span></span>
    </div>
  );
}

/* --------- Date activity picker --------- */
const DATE_OPTIONS = [
  "Pottery", "Escape Room", "Cooking Class", "Dessert Crawl",
  "Bookstore Date", "Café Date", "Flower Market", "Mini Golf", "Arcade",
];
function DatePicker() {
  const { dateChoice, setDateChoice } = useBdayState();
  return (
    <div>
      <div className="text-xs uppercase tracking-[0.24em] text-gold">Choose Today's Adventure</div>
      <p className="mt-2 text-sm text-muted-foreground">Pick together, in the moment.</p>
      <div className="mt-4 flex flex-wrap gap-2">
        {DATE_OPTIONS.map((opt) => {
          const active = dateChoice === opt;
          return (
            <button
              key={opt}
              onClick={() => setDateChoice(active ? null : opt)}
              className={`rounded-full border px-3 py-2.5 text-xs transition-all sm:px-4 sm:py-2 sm:text-sm ${
                active
                  ? "border-gold bg-gold text-warm-white shadow-soft"
                  : "border-blush-deep/40 bg-warm-white text-charcoal hover:border-blush-deep hover:bg-blush/40"
              }`}
            >
              {opt}
            </button>
          );
        })}
      </div>
      {dateChoice && (
        <p className="text-serif mt-4 text-lg italic text-charcoal">Today, we chose — {dateChoice}.</p>
      )}
    </div>
  );
}

/* --------- Journal --------- */
function Journal({ day }: { day: string }) {
  const { getJournal, setJournalText, uploadJournalPhoto, removeJournalPhoto } = useBdayState();
  const entry = getJournal(day);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setUploadError("Please choose an image file.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setUploadError("Image must be under 5 MB.");
      return;
    }

    setUploading(true);
    setUploadError(null);
    try {
      await uploadJournalPhoto(day, file);
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="glass-card rounded-2xl p-4 sm:rounded-3xl sm:p-6">
      <div className="flex items-center gap-2">
        <BookHeart className="h-4 w-4 text-blush-deep" />
        <div className="text-xs uppercase tracking-[0.24em] text-gold">Journal — {day}</div>
      </div>
      <textarea
        value={entry.text}
        onChange={(e) => setJournalText(day, e.target.value)}
        placeholder="Write a memory from today…"
        rows={4}
        className="mt-3 w-full resize-none rounded-2xl border border-blush/70 bg-warm-white/80 p-4 text-sm leading-relaxed text-charcoal placeholder:text-muted-foreground/70 focus:border-gold focus:outline-none focus:ring-2 focus:ring-gold/30"
      />

      {entry.images.length > 0 && (
        <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3">
          {entry.images.map((url) => (
            <div key={url} className="group relative aspect-square overflow-hidden rounded-2xl bg-blush/30">
              <img src={url} alt="" className="h-full w-full object-cover" loading="lazy" />
              <button
                type="button"
                onClick={() => void removeJournalPhoto(day, url)}
                className="absolute right-2 top-2 grid h-7 w-7 place-items-center rounded-full bg-charcoal/70 text-warm-white opacity-100 transition-opacity sm:opacity-0 sm:group-hover:opacity-100"
                aria-label="Remove photo"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="mt-4">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="sr-only"
          onChange={(e) => void handleFileChange(e)}
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-blush-deep/50 bg-warm-white/60 px-4 py-3 text-sm text-charcoal transition-colors hover:border-blush-deep hover:bg-blush/20 disabled:opacity-60 sm:w-auto"
        >
          <ImagePlus className="h-4 w-4" />
          {uploading ? "Uploading…" : "Add a photo"}
        </button>
        {uploadError && <p className="mt-2 text-xs text-red-600">{uploadError}</p>}
      </div>
    </div>
  );
}

/* ==================================================
   PAGE
   ================================================== */

// timeline item ids per day for progress
const DAY1_IDS = ["d1-flowers", "d1-drive", "d1-dinner", "d1-dessert", "d1-stay"];
const DAY2_IDS = ["d2-brunch", "d2-shopping", "d2-usa", "d2-canada", "d2-coffee", "d2-fam"];
const DAY3_IDS = ["d3-morning", "d3-brunch", "d3-massage", "d3-freetime", "d3-dinner", "d3-horses", "d3-golden"];
const ALL_IDS = [...DAY1_IDS, ...DAY2_IDS, ...DAY3_IDS];

const BdayIndex = () => {
  const { timeline, toggleTimeline, syncing, syncError, online } = useBdayState();
  const done = timeline;
  const toggle = toggleTimeline;
  const completedCount = ALL_IDS.filter((id) => done[id]).length;
  const percent = Math.round((completedCount / ALL_IDS.length) * 100);

  const scrollToBegin = () => document.getElementById("day-1")?.scrollIntoView({ behavior: "smooth" });

  return (
    <main className="relative min-h-screen overflow-x-hidden">
      <ProgressBar percent={percent} />
      <FloatingHearts />

      <div className="fixed bottom-4 right-4 z-50 rounded-full bg-warm-white/90 px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] text-charcoal/70 shadow-soft backdrop-blur sm:text-xs">
        {syncing ? "Saving…" : online ? "Synced" : "Offline"}
        {syncError && <span className="sr-only">{syncError}</span>}
      </div>

      {/* HERO */}
      <section className="relative isolate flex min-h-[85svh] flex-col items-center justify-center px-4 pb-16 pt-14 text-center sm:min-h-[100svh] sm:px-6 sm:pb-24 sm:pt-16">
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-blush/40 via-cream to-cream" />
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_top,_var(--blush)_0%,_transparent_55%)]" />

        <div className="animate-rise">
          <div className="text-[10px] uppercase tracking-[0.25em] text-charcoal/70 sm:text-xs sm:tracking-[0.4em]">A Three-Day Itinerary · July 7 — 9</div>
          <h1 className="text-serif mx-auto mt-5 max-w-4xl text-4xl font-semibold leading-[1.08] text-charcoal sm:mt-6 sm:text-6xl md:text-7xl">
            A Birthday Weekend
            <br />
            <span className="italic">for {HER_NAME}</span>
            <Heart className="ml-2 inline h-6 w-6 -translate-y-1 fill-blush-deep text-blush-deep sm:ml-3 sm:h-10 sm:w-10 sm:-translate-y-2" strokeWidth={0} />
          </h1>
          <p className="text-serif mx-auto mt-4 max-w-xl text-base italic text-charcoal/80 sm:mt-6 sm:text-xl">
            &ldquo;Three days dedicated to celebrating you.&rdquo;
          </p>
        </div>

        <div className="mt-8 w-full animate-rise sm:mt-10" style={{ animationDelay: "0.15s" }}>
          <Countdown />
        </div>

        <button
          onClick={scrollToBegin}
          className="mt-8 inline-flex w-full max-w-xs items-center justify-center gap-2 rounded-full bg-charcoal px-6 py-3.5 text-sm font-medium tracking-wide text-warm-white shadow-elegant transition-all active:scale-[0.98] sm:mt-10 sm:w-auto sm:px-8 sm:py-4 hover:bg-blush-deep"
        >
          <Sparkles className="h-4 w-4" />
          Begin Our Adventure
        </button>

        <div className="mt-10 text-[10px] uppercase tracking-[0.2em] text-charcoal/60 sm:mt-14 sm:text-xs sm:tracking-[0.3em]">
          Progress · {percent}% complete
        </div>
      </section>

      {/* DAY 1 */}
      <section id="day-1" className="relative scroll-mt-6 px-4 py-16 sm:scroll-mt-8 sm:px-6 sm:py-24 md:py-32">
        <SectionHeader day="Day One" date="July 7" theme="The Beginning" />
        <div className="mx-auto mt-10 max-w-2xl sm:mt-14 lg:max-w-3xl">
          <div className="relative">
            <div className="absolute left-3 top-4 bottom-4 w-px bg-gradient-to-b from-blush-deep via-gold/60 to-transparent sm:left-4" />
            <div className="space-y-4 sm:space-y-5">
              <TimelineCard time="Afternoon" emoji="🌸" title="Pick her up with flowers" id="d1-flowers" checked={!!done["d1-flowers"]} onToggle={() => toggle("d1-flowers")} description="Meet her at the door — hands full, heart fuller." />
              <TimelineCard time="Golden Hour" emoji="🚗" title="Drive to Buffalo" id="d1-drive" checked={!!done["d1-drive"]} onToggle={() => toggle("d1-drive")} description="Playlist on, windows down, no rush.">
                <MapPlaceholder place="Buffalo, NY" />
              </TimelineCard>
              <TimelineCard time="Evening" emoji="🍽️" title="Dinner together" id="d1-dinner" checked={!!done["d1-dinner"]} onToggle={() => toggle("d1-dinner")} description="Somewhere warm and slow — candlelight preferred." />
              <TimelineCard time="After Dinner" emoji="🍨" title="Dessert" id="d1-dessert" checked={!!done["d1-dessert"]} onToggle={() => toggle("d1-dessert")} description="Share one — steal each other's bites." />
              <TimelineCard time="Night" emoji="🏡" title="Stay at brother's house" id="d1-stay" checked={!!done["d1-stay"]} onToggle={() => toggle("d1-stay")} description="Home base for a soft landing." />
            </div>
            <blockquote className="text-serif mt-8 border-l-2 border-gold pl-4 text-lg italic text-charcoal/85 sm:mt-12 sm:pl-6 sm:text-2xl">
              &ldquo;The best adventures begin together.&rdquo;
            </blockquote>
          </div>
          <div className="mt-8 sm:mt-10">
            <Journal day="Day 1" />
          </div>
        </div>
      </section>

      {/* DAY 2 — Birthday */}
      <section id="day-2" className="relative scroll-mt-6 bg-gradient-to-b from-transparent via-blush/25 to-transparent px-4 py-16 sm:scroll-mt-8 sm:px-6 sm:py-24 md:py-32">
        <SectionHeader day="Day Two — The Birthday" date="July 8" theme="Spoil Her Day" quote="Today is all about you." />

        <div className="mx-auto mt-10 max-w-2xl sm:mt-14 lg:max-w-3xl">
          <div className="relative">
            <div className="absolute left-3 top-4 bottom-4 w-px bg-gradient-to-b from-blush-deep via-gold/60 to-transparent sm:left-4" />
            <div className="space-y-4 sm:space-y-5">
              <TimelineCard time="Morning" emoji="🥞" title="Brunch" id="d2-brunch" checked={!!done["d2-brunch"]} onToggle={() => toggle("d2-brunch")} description="Slow, buttery, unhurried." />
              <TimelineCard time="Midday" emoji="🛍" title="Shopping Spree — Main Gift" id="d2-shopping" checked={!!done["d2-shopping"]} onToggle={() => toggle("d2-shopping")} description="A whole day, a whole wardrobe. Everything on her." featured>
                <div className="space-y-3 sm:space-y-4">
                  <ShoppingCategory title="Summer" storageKey="shop-summer" items={["Baby T-shirts","Oversized T-shirts","Tank tops","Crop tops","Dresses","Skirts","Shorts","Linen pants"]} />
                  <ShoppingCategory title="Fall" storageKey="shop-fall" items={["Sweaters","Hoodies","Cardigans","Jeans","Jackets"]} />
                  <ShoppingCategory title="Winter" storageKey="shop-winter" items={["Knit sweaters","Puffer jacket","Wool coat","Boots"]} />
                  <ShoppingCategory title="Spring" storageKey="shop-spring" items={["Light jackets","Dresses","Cardigans"]} />
                  <ShoppingCategory title="Shoes" storageKey="shop-shoes" items={["Sneakers","Sandals","Slides","Heels"]} />
                  <ShoppingCategory title="Beauty" storageKey="shop-beauty" items={["Makeup","Skincare","Hair products"]} />
                </div>
              </TimelineCard>
              <TimelineCard time="Afternoon" emoji="🇺🇸" title="Fashion Outlets of Niagara Falls" id="d2-usa" checked={!!done["d2-usa"]} onToggle={() => toggle("d2-usa")} description="First stop, US side.">
                <MapPlaceholder place="Fashion Outlets of Niagara Falls, USA" />
              </TimelineCard>
              <TimelineCard time="Afternoon" emoji="🇨🇦" title="Outlet Collection at Niagara" id="d2-canada" checked={!!done["d2-canada"]} onToggle={() => toggle("d2-canada")} description="Cross the border, keep going.">
                <MapPlaceholder place="Outlet Collection at Niagara, Canada" />
              </TimelineCard>
              <TimelineCard time="Break" emoji="🍵" title="Coffee break" id="d2-coffee" checked={!!done["d2-coffee"]} onToggle={() => toggle("d2-coffee")} description="Cortado, matcha, whatever she loves today." />
              <TimelineCard time="Evening" emoji="👨‍👩‍👧" title="Family birthday dinner" id="d2-fam" checked={!!done["d2-fam"]} onToggle={() => toggle("d2-fam")} description="Cake, candles, the people she loves." />
            </div>
          </div>
          <div className="mt-8 sm:mt-10">
            <Journal day="Day 2" />
          </div>
        </div>
      </section>

      {/* DAY 3 */}
      <section id="day-3" className="relative scroll-mt-6 px-4 py-16 sm:scroll-mt-8 sm:px-6 sm:py-24 md:py-32">
        <SectionHeader day="Day Three" date="July 9" theme="Romance" />

        <div className="mx-auto mt-10 max-w-2xl sm:mt-14 lg:max-w-3xl">
          <div className="relative">
            <div className="absolute left-3 top-4 bottom-4 w-px bg-gradient-to-b from-blush-deep via-gold/60 to-transparent sm:left-4" />
            <div className="space-y-4 sm:space-y-5">
              <TimelineCard time="Morning" emoji="☕" title="Slow morning" id="d3-morning" checked={!!done["d3-morning"]} onToggle={() => toggle("d3-morning")} description="Nowhere to be." />
              <TimelineCard time="Late Morning" emoji="🥞" title="Brunch" id="d3-brunch" checked={!!done["d3-brunch"]} onToggle={() => toggle("d3-brunch")} description="Round two — earned." />
              <TimelineCard time="Midday" emoji="💆" title="Couples massage" id="d3-massage" checked={!!done["d3-massage"]} onToggle={() => toggle("d3-massage")} description="Soft lights, softer everything." />
              <TimelineCard time="Afternoon" emoji="🎨" title="Free Time / Date Activity" id="d3-freetime" checked={!!done["d3-freetime"]} onToggle={() => toggle("d3-freetime")}>
                <DatePicker />
              </TimelineCard>
              <TimelineCard time="Early Evening" emoji="🍽" title="Early dinner" id="d3-dinner" checked={!!done["d3-dinner"]} onToggle={() => toggle("d3-dinner")} description="Light plates — save room for the sunset." />
            </div>
          </div>
          <div className="mt-8 sm:mt-10">
            <Journal day="Day 3" />
          </div>
        </div>

        {/* FEATURED — Sunset Horseback Riding */}
        <div className="mx-auto mt-12 max-w-2xl sm:mt-16 lg:max-w-3xl">
          <div className="glass-card rounded-2xl border border-gold/50 p-5 shadow-elegant sm:rounded-3xl sm:p-8">
            <button
              onClick={() => toggle("d3-horses")}
              className={`mb-3 inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] transition-colors sm:mb-4 sm:text-xs sm:tracking-[0.24em] ${
                done["d3-horses"] ? "bg-gold text-warm-white" : "bg-gold/15 text-gold"
              }`}
            >
              {done["d3-horses"] ? <Check className="h-3 w-3" strokeWidth={3} /> : <Sun className="h-3 w-3" />}
              Featured Moment
            </button>
            <h3 className="text-serif text-2xl font-semibold text-charcoal sm:text-4xl">🐎 Sunset Horseback Riding</h3>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground sm:mt-3 sm:text-base">
              Two horses, one horizon. The kind of memory that photographs can&apos;t quite hold.
            </p>
          </div>

          <div className="mt-6 sm:mt-8">
            <TimelineCard time="Golden Hour" emoji="🌅" title="Golden Hour" id="d3-golden" checked={!!done["d3-golden"]} onToggle={() => toggle("d3-golden")} description="Watch the sunset together." />
          </div>
        </div>

        <div className="mx-auto mt-14 max-w-2xl px-2 text-center sm:mt-20 sm:max-w-3xl">
          <div className="gold-divider mx-auto w-20 sm:w-24" />
          <p className="text-serif mt-6 text-xl italic leading-relaxed text-charcoal sm:mt-8 sm:text-3xl">
            &ldquo;The best part of this weekend wasn&apos;t the places we went — it was spending it with you.&rdquo;
          </p>
          <Heart className="mx-auto mt-5 h-6 w-6 fill-blush-deep text-blush-deep animate-shimmer sm:mt-6" strokeWidth={0} />
        </div>
      </section>

      {/* FOOTER */}
      <footer className="relative border-t border-blush/60 bg-warm-white/60 px-4 py-10 text-center backdrop-blur sm:px-6 sm:py-12">
        <div className="mx-auto max-w-2xl">
          <Heart className="mx-auto h-6 w-6 fill-blush-deep text-blush-deep" strokeWidth={0} />
          <p className="text-serif mt-4 text-lg italic text-charcoal">
            Made for {HER_NAME}, with all my love.
          </p>
          <p className="mt-2 text-xs uppercase tracking-[0.28em] text-muted-foreground">July 7 — 9 · A keepsake</p>
        </div>
      </footer>
    </main>
  );
};

export default BdayIndex;

